import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './AdminTables.css';

const TABLE_TYPES = ['No-Limit', 'Pot-Limit', 'Limit', 'Spread-Limit'];

function TableRow({ table, onDelete, onEdit }) {
  const seatedCount = table.seats.filter(s => s.player).length;
  return (
    <div className="at-row">
      <div className="at-row-info">
        <span className="at-row-name">{table.name}</span>
        <span className={`type-badge ${{ 'No-Limit': 'nl', 'Pot-Limit': 'pl', 'Limit': 'limit', 'Spread-Limit': 'sl' }[table.type] ?? 'pl'}`}>{table.type}</span>
        <span className="at-row-stakes">{table.stakes}</span>
        <span className="at-row-seated">{seatedCount}/{table.max} seated</span>
        {table.gameState?.phase && table.gameState.phase !== 'waiting' && (
          <span className="at-row-phase">{table.gameState.phase}</span>
        )}
      </div>
      <div className="at-row-actions">
        <button className="at-btn at-btn-edit" onClick={() => onEdit(table)}>Edit</button>
        <button className="at-btn at-btn-delete" onClick={() => onDelete(table.id)}>Delete</button>
      </div>
    </div>
  );
}

function TableForm({ initial, onSave, onCancel }) {
  const [name,     setName]     = useState(initial?.name     ?? '');
  const [type,     setType]     = useState(initial?.type     ?? 'No-Limit');
  const [sb,       setSb]       = useState(initial?.config.smallBlind ?? 1);
  const [bb,       setBb]       = useState(initial?.config.bigBlind   ?? 2);
  const [ante,     setAnte]     = useState(initial?.config.ante       ?? 0);
  const [minBuy,   setMinBuy]   = useState(initial?.config.minBuyIn   ?? 40);
  const [maxBuy,   setMaxBuy]   = useState(initial?.config.maxBuyIn   ?? 400);
  const [handCap,  setHandCap]  = useState(initial?.config.handCap    ?? '');
  const [raiseCap, setRaiseCap] = useState(initial?.config.raiseCap   ?? '');
  const [spreadMin,setSpreadMin]= useState(initial?.config.spreadMin  ?? '');
  const [spreadMax,setSpreadMax]= useState(initial?.config.spreadMax  ?? '');
  const [holeCount,setHoleCount]= useState(initial?.config.holeCount  ?? 4);
  const [deckType, setDeckType] = useState(initial?.config.deckType   ?? 'tiles');
  const [allowStr, setAllowStr] = useState(initial?.config.allowStraddles ?? false);
  const [maxStr,   setMaxStr]   = useState(initial?.config.maxStraddleCount ?? 1);
  const [allow3b,  setAllow3b]  = useState(initial?.config.allowThirdBlind ?? false);
  const [botCount, setBotCount] = useState(initial?.botRoster?.length ?? 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    const stakes = `$${sb} / $${bb}`;
    const config = {
      smallBlind: +sb, bigBlind: +bb, ante: +ante,
      minBuyIn: +minBuy, maxBuyIn: +maxBuy, shortBuy: +(minBuy / 2),
      allowStraddles: allowStr, maxStraddleCount: +maxStr,
      allowThirdBlind: allow3b,
      raiseCap:  raiseCap  !== '' ? +raiseCap  : null,
      handCap:   handCap   !== '' ? +handCap   : null,
      spreadMin: spreadMin !== '' ? +spreadMin : undefined,
      spreadMax: spreadMax !== '' ? +spreadMax : undefined,
      holeCount: +holeCount,
      deckType,
      allowSeatChanges: true,
    };
    onSave({ name, type, stakes, config, botCount: +botCount });
  };

  return (
    <form className="at-form" onSubmit={handleSubmit}>
      <div className="at-form-grid">
        <label>Name<input value={name} onChange={e => setName(e.target.value)} required /></label>
        <label>Type
          <select value={type} onChange={e => setType(e.target.value)}>
            {TABLE_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label>Small Blind<input type="number" value={sb} onChange={e => setSb(e.target.value)} min="0" step="any" /></label>
        <label>Big Blind<input type="number" value={bb} onChange={e => setBb(e.target.value)} min="0" step="any" /></label>
        <label>Ante<input type="number" value={ante} onChange={e => setAnte(e.target.value)} min="0" step="any" /></label>
        <label>Min Buy-In<input type="number" value={minBuy} onChange={e => setMinBuy(e.target.value)} min="0" step="any" /></label>
        <label>Max Buy-In<input type="number" value={maxBuy} onChange={e => setMaxBuy(e.target.value)} min="0" step="any" /></label>
        <label>Hand Cap<input type="number" value={handCap} onChange={e => setHandCap(e.target.value)} placeholder="none" min="0" step="any" /></label>
        <label>Raise Cap<input type="number" value={raiseCap} onChange={e => setRaiseCap(e.target.value)} placeholder="none" min="0" step="1" /></label>
        {type === 'Spread-Limit' && <>
          <label>Spread Min<input type="number" value={spreadMin} onChange={e => setSpreadMin(e.target.value)} min="0" step="any" /></label>
          <label>Spread Max<input type="number" value={spreadMax} onChange={e => setSpreadMax(e.target.value)} min="0" step="any" /></label>
        </>}
        <label>Hole Count
          <select value={holeCount} onChange={e => setHoleCount(e.target.value)}>
            <option value={4}>4 (Standard)</option>
            <option value={5}>5 (Five-Tile Hi)</option>
          </select>
        </label>
        <label>Deck Type
          <select value={deckType} onChange={e => setDeckType(e.target.value)}>
            <option value="tiles">Mahjong Tiles</option>
            <option value="cards">Playing Cards</option>
          </select>
        </label>
        <label>Starting Bots
          <select value={botCount} onChange={e => setBotCount(e.target.value)}>
            {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="at-checkbox-label">
          <input type="checkbox" checked={allowStr} onChange={e => setAllowStr(e.target.checked)} />
          Allow Straddles
        </label>
        {allowStr && (
          <label>Max Straddles<input type="number" value={maxStr} onChange={e => setMaxStr(e.target.value)} min="1" max="3" step="1" /></label>
        )}
        <label className="at-checkbox-label">
          <input type="checkbox" checked={allow3b} onChange={e => setAllow3b(e.target.checked)} />
          Allow Third Blind
        </label>
      </div>
      <div className="at-form-actions">
        <button type="button" className="at-btn at-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="at-btn at-btn-save">{initial ? 'Save Changes' : 'Create Table'}</button>
      </div>
    </form>
  );
}

function PlayerWallets({ token }) {
  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const [players, setPlayers] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [busy,    setBusy]    = useState({});
  const [msg,     setMsg]     = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const { users } = await res.json(); setPlayers(users); }
    } catch { /* ignore */ }
  }, [API, token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const adjust = async (userId, delta) => {
    if (!delta || isNaN(delta)) return;
    setBusy(b => ({ ...b, [userId]: true }));
    setMsg(m => ({ ...m, [userId]: '' }));
    try {
      const res = await fetch(`${API}/auth/wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, delta: +delta }),
      });
      if (res.ok) {
        const { wallet } = await res.json();
        setPlayers(ps => ps.map(p => p.id === userId ? { ...p, wallet } : p));
        setAmounts(a => ({ ...a, [userId]: '' }));
        setMsg(m => ({ ...m, [userId]: delta > 0 ? `+$${Math.abs(delta)} added` : `-$${Math.abs(delta)} removed` }));
      } else {
        const { message } = await res.json();
        setMsg(m => ({ ...m, [userId]: message || 'Error' }));
      }
    } catch { setMsg(m => ({ ...m, [userId]: 'Network error' })); }
    setBusy(b => ({ ...b, [userId]: false }));
  };

  return (
    <div className="at-wallets">
      {players.length === 0 && <p className="at-empty">No players found.</p>}
      {players.map(p => (
        <div key={p.id} className="at-wallet-row">
          <div className="at-wallet-info">
            <span className="at-wallet-name">{p.name}</span>
            <span className="at-wallet-username">@{p.username}</span>
          </div>
          <span className="at-wallet-balance">${p.wallet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <div className="at-wallet-controls">
            <input
              type="number"
              className="at-wallet-input"
              placeholder="Amount"
              min="0"
              step="any"
              value={amounts[p.id] || ''}
              onChange={e => setAmounts(a => ({ ...a, [p.id]: e.target.value }))}
            />
            <button
              className="at-btn at-btn-add"
              disabled={busy[p.id]}
              onClick={() => adjust(p.id, +amounts[p.id])}
            >Add</button>
            <button
              className="at-btn at-btn-sub"
              disabled={busy[p.id]}
              onClick={() => adjust(p.id, -(+amounts[p.id]))}
            >Remove</button>
          </div>
          {msg[p.id] && <span className={`at-wallet-msg ${msg[p.id].startsWith('+') ? 'pos' : msg[p.id].startsWith('-') ? 'neg' : 'err'}`}>{msg[p.id]}</span>}
        </div>
      ))}
    </div>
  );
}

export default function AdminTables() {
  const { user, token } = useAuth();
  const { tables, socket } = useSocket();
  const navigate = useNavigate();
  const [editing, setEditing]   = useState(null); // table being edited
  const [creating, setCreating] = useState(false);

  if (!user || user.role !== 'admin') {
    return (
      <div className="at-page">
        <div className="at-access-denied">Admin access required.</div>
      </div>
    );
  }

  const handleDelete = (tableId) => {
    if (!window.confirm('Delete this table? This cannot be undone.')) return;
    socket?.emit('table:delete', { tableId });
  };

  const handleCreate = (config) => {
    socket?.emit('table:create', config);
    setCreating(false);
  };

  const handleEdit = (updates) => {
    socket?.emit('table:update', { tableId: editing.id, updates });
    setEditing(null);
  };

  return (
    <div className="at-page">
      <div className="at-header">
        <button className="at-back-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        <h1 className="at-title">Manage Tables</h1>
        <button className="at-btn at-btn-create" onClick={() => setCreating(true)}>+ New Table</button>
      </div>

      {creating && (
        <div className="at-section">
          <h2 className="at-section-title">New Table</h2>
          <TableForm onSave={handleCreate} onCancel={() => setCreating(false)} />
        </div>
      )}

      {editing && (
        <div className="at-section">
          <h2 className="at-section-title">Edit: {editing.name}</h2>
          <TableForm initial={editing} onSave={handleEdit} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="at-section">
        <h2 className="at-section-title">All Tables <span className="at-count">{tables.length}</span></h2>
        {tables.length === 0 && <p className="at-empty">No tables yet.</p>}
        {tables.map(t => (
          <TableRow
            key={t.id}
            table={t}
            onDelete={handleDelete}
            onEdit={(t) => { setEditing(t); setCreating(false); }}
          />
        ))}
      </div>

      <div className="at-section">
        <h2 className="at-section-title">Player Wallets</h2>
        <PlayerWallets token={token} />
      </div>
    </div>
  );
}
